<template>
  <section>
    <FormulateForm
      ref="orderForm"
      name="orderForm"
    >
      <b-card-actions
        :show-loading="loading"
        action-collapse
        :title="dataCardTitle"
      >
        <b-row v-if="isEdit">
          <b-col md="2">
            <FormulateInput
              id="order_maintain-status"
              v-model="order.status"
              type="label"
              :label="$t('Status Venda')"
              :options="saleStatusOptions()"
              is-badge
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="order_maintain-delivery_status"
              v-model="order.deliveryStatus"
              type="label"
              :label="$t('Status Entrega')"
              :options="deliveryStatusOptions()"
              is-badge
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="order_maintain-origin"
              :value="saleOriginLabel[order.origin] || '-'"
              type="label"
              :label="$t('Origem')"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="order_maintain-created-date"
              :value="order.createdDate"
              type="label"
              filter="datetime"
              :label="$t('Data de Criação')"
            />
          </b-col>
          <template v-if="order.voucher">
            <b-col md="2">
              <FormulateInput
                id="order_maintain-voucher-applied"
                type="label"
                :label="$t('Cupom de desconto')"
                :value="
                  $t(`${order.voucher.type === 'Percent' ? '%' : 'R$'} ${order.voucher.value}`)
                "
                :instructions="[
                  {
                    text: $t(`Código: ${order.voucher.code}`),
                  },
                  {
                    text: $t(`Descrição: ${order.voucher.description}`),
                  },
                ]"
                :attributes="{
                  title: 'Informações adicionais',
                }"
              />
            </b-col>
          </template>
          <b-col
            v-if="order.invoice"
            md="2"
          >
            <FormulateInput
              id="order_maintain-invoice-id"
              :value="`${order.invoice.invoiceNumber} | ${
                invoiceModelLabelShort[order.invoice.model]
              }`"
              type="label"
              :label="$t('Nota fiscal')"
              router-name="invoice-maintain"
              :router-params="{ id: order.invoice.id }"
              :router-title="`${$t('Visualizar nota fiscal')} - ${
                statusInvoiceLabel[order.invoice.status]
              }`"
            />
          </b-col>
          <b-col
            v-if="order.approvalFlowTransaction"
            md="2"
          >
            <FormulateInput
              id="order_maintain-approval-id"
              :value="`${order.approvalFlowTransaction.id} | ${
                approvalFlowLabel[order.approvalFlowTransaction.status]
              } `"
              type="label"
              :label="$t('Fluxo de Aprovação')"
              router-name="approval-flow-details"
              :router-params="{ id: order.approvalFlowTransaction.id }"
              :router-title="`${$t('Visualizar aprovação')} - ${
                approvalFlowLabel[order.approvalFlowTransaction.status]
              }`"
            />
          </b-col>
          <b-col
            v-if="order.barTabConsumptionId"
            md="2"
          >
            <FormulateInput
              id="order_maintain-consumption_id"
              v-model="order.barTabConsumptionId"
              :label="$t('Consumo')"
              type="label"
              :icons="consumptionIdLabelIcons"
              @link="onShowConsumptionSidebar"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <!-- alteração do valor é feito no método onSelectStore -->
            <e-store-combo
              id="order_maintain-store_id"
              :value="order.storeId"
              :required="true"
              :clearable="false"
              :disabled="isReadOnly || isEdit"
              :only-active-options="true"
              @input="onSelectStore"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="order_maintain-price_table"
              v-model="order.priceTableId"
              type="label"
              :label="$t('Tabela de preço')"
              :options="getComboPriceTables"
              class="required"
              validation="required"
              :instruction="$t('Tabela de preço do cliente no momento que o pedido foi criado.')"
            />
          </b-col>

          <b-col md="2">
            <FormulateInput
              v-if="!(isReadOnly || isConsignedWithPayment || isEdit)"
              id="order_maintain-is_consigned"
              v-model="order.isConsigned"
              type="switch"
              :label="$t('Consignado?')"
              :disabled="isReadOnly"
              @input="onChangeConsign"
            />

            <div
              v-else
              style="max-width: 100px"
            >
              <p class="h6">
                {{ $t('Consignado?') }}
              </p>
              <e-status-badge
                :status="!!order.isConsigned"
                type="yesNo"
                :title="
                  !isReadOnly ? $t('SALE_ORDER.UPDATE_ERROR.CONSIGNED_CANNOT_BE_CHANGED') : ''
                "
              />
            </div>
          </b-col>

          <b-col md="2">
            <e-slot-authenticated
              id="order-auth_discount_type"
              v-model="order.discountAuth"
              :delegable-permission="discountPermission"
              :store-id="order.store ? order.store.id : 0"
              :is-valid-store="!!order.store"
              :is-read-only="isReadOnly"
            >
              <template #content="slotProps">
                <FormulateInput
                  id="order-discount_type"
                  v-model="order.discountType"
                  type="radio"
                  :options="{
                    [discountTypesEnum.VALUE]: 'R$',
                    [discountTypesEnum.PERCENTAGE]: '%',
                  }"
                  :label="$t('Desconto em')"
                  :element-class="['d-flex', 'mt-1']"
                  :disabled="slotProps.isReadOnly"
                />
              </template>
            </e-slot-authenticated>
          </b-col>

          <b-col md="2">
            <e-slot-authenticated
              v-model="order.discountAuth"
              :delegable-permission="discountPermission"
              :store-id="order.store ? order.store.id : 0"
              :is-valid-store="!!order.store"
              :is-read-only="isReadOnly"
            >
              <template #content="slotProps">
                <FormulateInput
                  v-if="slotProps.isReadOnly"
                  id="order-discount"
                  v-model="order.discount"
                  name="discount_percentage"
                  type="label"
                  :label="`${$t('Desconto')} (${getDiscountSymbol})`"
                  :filter="isDiscountPercent ? 'percentage' : 'currency'"
                  :instruction="$t('Desconto do pedido')"
                />
                <FormulateInput
                  v-else
                  id="order-discount"
                  v-model="order.discount"
                  name="discount_percentage"
                  :type="isDiscountPercent ? 'text-percetage' : 'text-number'"
                  :label="`${$t('Desconto')} (${getDiscountSymbol})`"
                  :currency="getDiscountSymbol"
                  :currency-symbol-position="isDiscountPercent ? 'suffix' : 'prefix'"
                  :precision="2"
                  :instruction="$t('Desconto do pedido')"
                  :disabled="isReadOnly"
                />
              </template>
            </e-slot-authenticated>
          </b-col>

          <b-col md="2">
            <FormulateInput
              id="order_maintain-echope_classification"
              :value="orderEchopeClassification"
              type="label"
              :label="$t('Classificação echope')"
              :options="echopeClassificationOptions()"
              :instruction="$t('Definida pela tabela de preço do pedido.')"
            />
          </b-col>

          <b-col md="2">
            <FormulateInput
              v-if="!isEdit"
              id="order_maintain-is_delivery"
              v-model="order.isDelivery"
              type="switch"
              :label="$t('Entrega?')"
              :disabled="isReadOnly"
              @input="onChangeDelivery"
            />

            <div
              v-else
              style="max-width: 100px"
            >
              <p class="h6">
                {{ $t('Entrega?') }}
              </p>
              <e-status-badge
                :status="!!order.isDelivery"
                type="yesNo"
                :title="titleNotEditDelivery"
              />
            </div>
          </b-col>

          <template v-if="showDeliveryData">
            <b-col md="2">
              <FormulateInput
                id="order_maintain-delivery_date"
                v-model="order.deliveryDate.date"
                type="datepicker"
                no-flip
                :min="today"
                :placeholder="$t('dd/mm/aaaa')"
                :label="$t('Data de entrega')"
                class="required"
                validation="required"
                :disabled="isReadOnly || (isEdit && !isLendingSchedule)"
                :date-disabled-fn="disabledDeliveryDate"
                @blur="onBlurDeliveryDate"
              />
            </b-col>
            <b-col md="2">
              <FormulateInput
                id="order_maintain-delivery_time"
                v-model="order.deliveryDate.time"
                type="timepicker"
                :min="today"
                :placeholder="$t('hh:mm')"
                :label="$t('Hora de entrega')"
                :instruction="deliveryTimeInstruction"
                :disabled="isReadOnly"
                @blur="onBlurDeliveryTime"
              />
            </b-col>
            <b-col>
              <FormulateInput
                id="order_maintain-delivery_observation"
                v-model="order.deliveryObservation"
                type="textarea"
                :label="$t('Observação da entrega')"
                :disabled="isReadOnly"
              />
            </b-col>
          </template>
        </b-row>

        <!-- Agenda de entrega de chope/comodato: os horários riscados estão ocupados ou fora da
             antecedência mínima, mas a loja pode escolhê-los (ou digitar um horário quebrado). -->
        <b-row v-if="showLendingSlots">
          <b-col>
            <p class="h6 mb-50">
              {{ $t('Horários de entrega do chope/comodato') }}
              <small
                v-if="lendingSlotsDay.installationMinutes"
                class="text-muted"
              >
                ({{ $t('Tempo de instalação') }}: {{ lendingSlotsDay.installationMinutes }} min)
              </small>
            </p>
            <p
              v-if="!lendingSlotsDay.slots.length"
              class="text-muted"
            >
              {{ $t('Não há horários de agendamento para a data escolhida.') }}
            </p>
            <div
              v-else
              class="d-flex flex-wrap"
            >
              <b-button
                v-for="slot in lendingSlotsDay.slots"
                :id="`order_maintain-lending_slot-${slotTime(slot.start)}`"
                :key="slot.start"
                size="sm"
                class="mr-50 mb-50"
                :class="{ 'lending-slot-unavailable': !slot.available }"
                :variant="isSelectedLendingSlot(slot) ? 'primary' : 'outline-secondary'"
                :title="lendingSlotTitle(slot)"
                :disabled="isReadOnly"
                @click="onSelectLendingSlot(slot)"
              >
                {{ slotTime(slot.start) }} - {{ slotTime(slot.end) }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        v-if="showCancelData"
        no-actions
        :title="$t('Dados do cancelamento')"
      >
        <b-row>
          <b-col md="3">
            <FormulateInput
              id="order_maintain-canceled_date"
              v-model="order.cancelationDate"
              :label="$t('Data/Hora')"
              type="label"
              filter="datetime"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="order_maintain-canceled_user"
              v-model="getCancelationUser"
              :label="$t('Usuário que cancelou')"
              type="label"
            />
          </b-col>

          <b-col>
            <FormulateInput
              id="order_maintain-cancel_observation"
              v-model="order.cancelationReason"
              :label="$t('Motivo')"
              type="label"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-row>
        <b-col :md="showDeliveryAddressCard ? 6 : 12">
          <b-card-actions
            :show-loading="loading"
            action-collapse
            :title="$t('Dados do Cliente')"
          >
            <template slot="title">
              <b-card-title class="card-title">
                {{ $t('Dados do Cliente') }}
              </b-card-title>
              <b-card-sub-title
                v-if="order.customerBiometricsValidationFlow && order.customerBiometricsValidationFlow.status == 'FINISHED'"
              >
                <b-badge variant="success">
                  {{ $t('Cliente validado com biometria') }}
                </b-badge>
              </b-card-sub-title>
            </template>
            <b-row>
              <b-col md="8">
                <e-person-search
                  id="order_maintain-customer"
                  v-model="order.customerId"
                  is-customer
                  :label="$t('Cliente')"
                  required
                  :is-read-only="isReadOnly && !clientChangeEnabled"
                  :default-options="customerDefaultOptions"
                  @input="onSelectCustomer"
                />
              </b-col>
              <template v-if="order.customer">
                <b-col md="4">
                  <FormulateInput
                    id="order_maintain-document"
                    v-model="order.customer.document"
                    type="label"
                    :label="$t('CPF/CNPJ')"
                    filter="cpfCnpj"
                  />
                </b-col>
                <b-col md="6">
                  <FormulateInput
                    id="order_maintain-email"
                    v-model="order.customer.email"
                    type="label"
                    :label="$t('Email')"
                  />
                </b-col>
                <b-col md="3">
                  <FormulateInput
                    id="order_maintain-phone"
                    v-model="getCustomerTelephone.phone"
                    type="label"
                    :label="$t('Telefone')"
                    filter="phone"
                  />
                </b-col>
                <b-col md="3">
                  <FormulateInput
                    id="order_maintain-cellphone"
                    v-model="getCustomerTelephone.cellphone"
                    type="label"
                    :label="$t('Celular')"
                    filter="phone"
                  />
                </b-col>

                <!-- Saldo ecash (somente na criação do pedido) -->
                <b-col
                  v-if="isCreating"
                  md="6"
                >
                  <FormulateInput
                    id="order_maintain-reward_balance"
                    :value="formatRewardBalance(rewardBalance)"
                    type="label"
                    :label="$t('Saldo ecash')"
                    :class="{ 'text-success': hasRewardBalance }"
                  />
                </b-col>

                <b-col
                  v-if="hasRewardBalance && !isReadOnly"
                  md="6"
                >
                  <e-slot-authenticated
                    v-if="isJuridicalCustomer"
                    v-model="ecashDiscountAuth"
                    :delegable-permission="applyEcashPermission"
                    :store-id="order.storeId"
                    :is-valid-store="!!order.storeId"
                  >
                    <template #content="slotProps">
                      <FormulateInput
                        id="order_maintain-use_reward_credits-switch"
                        v-model="order.useRewards"
                        type="switch"
                        :label="$t('Usar créditos ecash?')"
                        :disabled="slotProps.isReadOnly"
                        @input="applyRewardCredits"
                      />
                    </template>
                  </e-slot-authenticated>
                  <FormulateInput
                    v-else
                    id="order_maintain-use_reward_credits-switch"
                    v-model="order.useRewards"
                    type="switch"
                    :label="$t('Usar créditos ecash?')"
                    @input="onToggleRewardCredits"
                  />
                </b-col>

                <b-col
                  v-if="isDelivery"
                  md="12"
                >
                  <FormulateInput
                    id="order_maintain-address_options"
                    v-model="order.addressSelected"
                    type="vue-select"
                    :label="$t('Endereço para entrega')"
                    :options="getCustomerAddressOptions"
                    :clearable="false"
                    class="required"
                    validation="required"
                    :placeholder="$t('Selecione')"
                    :disabled="isReadOnly"
                    @input="changeAddress"
                  />
                </b-col>
              </template>
            </b-row>
            <b-row class="mt-1">
              <b-col
                md="12"
                align="right"
              >
                <e-button
                  v-if="clientChangeEnabled"
                  id="cancel_change_client"
                  type="button"
                  class="mr-2"
                  variant="outline-danger"
                  icon="x-circle"
                  :text="$t('Cancelar')"
                  @click="cancelChangeClient"
                />
                <e-button
                  v-if="isReadOnly"
                  id="change_client"
                  type="button"
                  variant="primary"
                  :icon="clientChangeEnabled ? 'check-circle' : 'pencil'"
                  :text="clientChangeEnabled ? $t('Confirmar alteração') : $t('Alterar cliente')"
                  :disabled="!$can('ChangeCustomer', 'Sale')"
                  :title="
                    !$can('ChangeCustomer', 'Sale')
                      ? $t('Você não possui permissão para alterar o cliente')
                      : ''
                  "
                  @click="changeClient"
                />
                <e-button
                  id="create_customer"
                  type="button"
                  variant="primary"
                  :icon="'plus-circle'"
                  :text="$t('Novo Cliente')"
                  :disabled="!$can('Create', 'Customer')"
                  :title="
                    !$can('Create', 'Customer')
                      ? $t('Você não possui permissão para criar cliente')
                      : ''
                  "
                  class="ml-1"
                  @click="createCustomer"
                />
              </b-col>
            </b-row>
          </b-card-actions>
        </b-col>

        <b-col
          v-if="showDeliveryAddressCard"
          md="6"
        >
          <e-address
            v-if="!isReadOnly"
            v-model="order.deliveryAddress"
            :show-loading="loading"
          />
          <b-card-actions
            v-else
            no-actions
            :title="$t('Endereço')"
          >
            <e-address
              v-model="order.deliveryAddress"
              :show-loading="loading"
              :read-only="true"
            />
          </b-card-actions>
        </b-col>
      </b-row>

      <b-row v-if="isDelivery">
        <b-col>
          <b-card-actions
            :show-loading="loading"
            action-collapse
            :title="$t('Dados de entrega')"
          >
            <b-row>
              <b-col md="3">
                <FormulateInput
                  id="order_maintain-delivery_fee_type"
                  v-model="deliveryData.deliveryFeeType"
                  type="radio"
                  :options="deliveryFeeLabel"
                  :label="$t('Tipo da taxa de entrega')"
                  :element-class="['d-flex', 'mt-1']"
                  :disabled="!canEditDelivery"
                  :title="titleNotEditDelivery"
                  @input="onChangeDeliveryFeeType"
                />
              </b-col>

              <b-col
                v-if="
                  order.origin === saleOriginEnum.APP || order.origin === saleOriginEnum.WEBSITE
                "
                md="2"
              >
                <FormulateInput
                  id="order_maintain-is_tax_zero"
                  :value="order.isTaxZero"
                  type="label"
                  :label="$t('Taxa zero?')"
                  :options="yesNoTypes()"
                  is-badge
                />
              </b-col>

              <b-col
                v-if="isReadOnly"
                md="3"
              >
                <FormulateInput
                  id="order-charged_delivery_fee"
                  :value="order.isTaxZero ? 0 : order.deliveryTax"
                  type="label"
                  :label="$t('Valor da taxa de entrega cobrada')"
                  filter="currency"
                  :instruction="$t('Valor da taxa efetivamente cobrada neste pedido.')"
                />
              </b-col>

              <template v-if="isDeliveryPerKilometer">
                <b-col md="3">
                  <FormulateInput
                    id="order-fee_per_kilometer_value"
                    v-model="deliveryData.shippingFeePerKilometer"
                    type="label"
                    :label="$t('Valor da taxa por KM da loja')"
                    filter="currency"
                    :instruction="
                      $t('Valor da taxa por quilômetro configurada para a loja selecionada.')
                    "
                  />
                </b-col>
                <b-col md="3">
                  <FormulateInput
                    id="order-distance_value"
                    v-model="deliveryData.distanceValue"
                    type="text-number"
                    :precision="2"
                    :label="$t('Distância em KM')"
                    :validation="`required|min:${minKilometerDelivery}`"
                    class="required"
                    :instructions="[
                      { text: $t('Distância em quilômetros da loja até o endereço de entrega.') },
                      {
                        text: $t('SALE.UI.MIN_DELIVERY_KM', {
                          minKilometer: minKilometerDelivery,
                        }),
                      },
                    ]"
                    :disabled="!canEditDelivery"
                    :title="titleNotEditDelivery"
                    @blur="onUpdateDelivery"
                  />
                </b-col>
              </template>

              <template v-else>
                <b-col
                  v-if="!isReadOnly && (!deliveryData.tiers || !deliveryData.tiers.length)"
                  md="3"
                >
                  <FormulateInput
                    id="order-single_fee_value"
                    v-model="deliveryData.shippingFee"
                    type="label"
                    :label="$t('Valor da taxa da loja')"
                    filter="currency"
                    :instruction="$t('Valor da taxa configurada para a loja selecionada.')"
                  />
                </b-col>
              </template>

              <b-col md="3">
                <e-slot-authenticated
                  v-model="deliveryData.discountAuth"
                  :delegable-permission="deliveryDiscountPermission"
                  :store-id="order.store ? order.store.id : 0"
                  :is-valid-store="!!order.store"
                  :is-read-only="isReadOnly"
                >
                  <template #content="slotProps">
                    <FormulateInput
                      v-if="slotProps.isReadOnly"
                      id="order-delivery_discount"
                      v-model="deliveryData.discount"
                      name="discount_percentage"
                      type="label"
                      :label="$t('Desconto da taxa da loja')"
                      filter="currency"
                      :instruction="
                        $t('Valor de desconto aplicado apenas sobre o valor da entrega')
                      "
                    />
                    <FormulateInput
                      v-else
                      id="order-delivery_discount"
                      v-model="deliveryData.discount"
                      name="discount_percentage"
                      type="text-number"
                      :label="$t('Desconto da taxa de entrega')"
                      :currency="$t('R$')"
                      :validation="[['optional'], ['max', deliveryData.deliveryTax]]"
                      :precision="2"
                      :instruction="
                        $t('Valor de desconto aplicado apenas sobre o valor da entrega')
                      "
                      :disabled="!canEditDelivery"
                      :title="titleNotEditDelivery"
                    />
                  </template>
                </e-slot-authenticated>
              </b-col>

              <b-col
                v-if="
                  !isReadOnly &&
                    !isDeliveryPerKilometer &&
                    deliveryData.tiers &&
                    deliveryData.tiers.length
                "
                md="6"
                class="mt-1"
              >
                <delivery-tiers-preview
                  :tiers="deliveryData.tiers"
                  :base-value="getDeliveryTierBaseValue"
                />
                <b-alert
                  show
                  variant="warning"
                  class="mt-1"
                >
                  <div class="alert-body">
                    {{
                      $t(
                        'Faixas vigentes atualmente para esta data de entrega. Podem diferir das aplicadas quando o pedido foi criado.'
                      )
                    }}
                  </div>
                </b-alert>
              </b-col>

              <b-col
                v-if="hasMotoboy"
                md="12"
              >
                <b-row>
                  <b-col md="4">
                    <FormulateInput
                      :value="motoboyName"
                      type="label"
                      :label="$t('Motoboy')"
                      :instruction="$t('Motoboy próprio designado para a entrega deste pedido')"
                    />
                  </b-col>
                  <b-col md="3">
                    <FormulateInput
                      :value="motoboyTelephone"
                      type="label"
                      :label="$t('Telefone do Motoboy')"
                    />
                  </b-col>
                  <b-col md="3">
                    <FormulateInput
                      :value="motoboyDocument"
                      type="label"
                      :label="$t('Documento do Motoboy')"
                    />
                  </b-col>
                </b-row>
              </b-col>

              <b-col md="12">
                <b-row v-if="order.deliveryOrder">
                  <b-col md="2">
                    <FormulateInput
                      :value="order.deliverySupplier ? order.deliverySupplier.name : '-'"
                      type="label"
                      :label="$t('Serviço de entrega')"
                    />
                  </b-col>
                  <b-col md="2">
                    <FormulateInput
                      v-model="order.deliveryOrderId"
                      type="label"
                      :label="$t('Id do pedido de entrega')"
                    />
                  </b-col>
                  <b-col md="2">
                    <FormulateInput
                      :value="$t(`PAY_BOX_SALE.DELIVERY_STATUS.${order.deliveryOrder.status}`)"
                      type="label"
                      :label="$t('Status da entrega')"
                    /></b-col>
                  <b-col md="6">
                    <FormulateInput
                      v-if="
                        order.deliveryOrder.driverInfo && order.deliveryOrder.driverInfo.vehicleInfo
                      "
                      :value="`${order.deliveryOrder.driverInfo.name} - ${$t('Placa/Cor')}: ${
                        order.deliveryOrder.driverInfo.vehicleInfo.plateNo
                      } / ${order.deliveryOrder.driverInfo.vehicleInfo.color} - ${$t(
                        'Telefone'
                      )}: ${$options.filters.phone(order.deliveryOrder.driverInfo.phone)}`"
                      type="label"
                      :label="$t('Dados do entregador')"
                    /></b-col>
                  <b-col md="3">
                    <FormulateInput
                      :value="
                        order.deliveryOrder.verifyInfo
                          ? order.deliveryOrder.verifyInfo.pickupVerifyCode
                          : '-'
                      "
                      type="label"
                      :label="$t('Código de verificação para o motorista')"
                      :instruction="
                        $t(
                          'Código de verificação que o motorista deverá fornecer à loja no momento da retirada dos produtos'
                        )
                      "
                    /></b-col>
                  <b-col md="3">
                    <FormulateInput
                      :value="
                        order.deliveryOrder.verifyInfo
                          ? order.deliveryOrder.verifyInfo.dropoffVerifyCode
                          : '-'
                      "
                      type="label"
                      :label="$t('Código de verificação para o cliente')"
                      :instruction="
                        $t(
                          'Código de verificação que o cliente deverá fornecer ao entregador no momento da entrega'
                        )
                      "
                    />
                  </b-col>
                </b-row>
              </b-col>

              <b-col
                v-if="order.lendingConfirmationDeliveryCode"
                md="12"
              >
                <b-row>
                  <b-col md="3">
                    <FormulateInput
                      :value="order.lendingConfirmationDeliveryCode"
                      type="label"
                      :label="$t('Código de verificação do cliente')"
                      :instruction="
                        $t(
                          'Código de verificação que o cliente deverá fornecer ao entregador no momento da entrega'
                        )
                      "
                    /></b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row class="mt-1">
              <b-col
                md="12"
                align="right"
              >
                <e-button
                  v-if="!order.deliveryOrder && order.id"
                  id="ask_delivery"
                  type="button"
                  class="mr-2"
                  variant="primary"
                  icon="truck"
                  :text="$t('Solicitar entrega')"
                  @click="onAskDeliveryService"
                />
              </b-col>
            </b-row>
          </b-card-actions>
        </b-col>
      </b-row>

      <b-card-actions
        v-if="order.customer && order.storeId"
        :show-loading="loading || loadingProducts"
        action-collapse
        :title="$t('Produtos')"
      >
        <b-row>
          <b-col
            v-if="!isReadOnly"
            md="12"
            class="mb-1 d-flex justify-content-end"
          >
            <e-button
              data-testid="btnAddProduct"
              :text="$t('Adicionar produto')"
              variant="primary"
              @click="() => onShowProductSidebar()"
            />
          </b-col>

          <b-col md="12">
            <sale-product-table
              :items="getProducts"
              :is-read-only="isReadOnly"
              :show-remove-when-exist-item="!isConsignedWithPayment"
              :show-update-when-exist-item="!isConsignedWithPayment"
              :show-update="true"
              :show-remove="true"
              @show-sidebar-update="onShowProductSidebar"
              @remove-item="onRemoveProduct"
            />
          </b-col>
          <b-col
            v-if="hasColdOptionProducts || order.coldOption"
            md="12"
          >
            <FormulateInput
              id="order-cold-option"
              v-model="order.coldOption"
              :type="isReadOnly ? 'label' : 'radio'"
              :options="coldOptionOptions()"
              :label="$t('Estado dos produtos')"
              :element-class="['d-flex', 'mt-1', 'flex-wrap']"
              class="required"
              validation="required"
            />
          </b-col>
        </b-row>

        <b-row>
          <b-col class="d-flex justify-content-end">
            <div>
              <p
                v-if="isDelivery"
                class="h4 text-right"
              >
                <e-instructions :title="$t('Taxa de entrega')" />
                {{ $t('Taxa de entrega:') }}
                <span class="h3">
                  {{ getOrderTotalDeliveryTax | currency }}
                </span>
              </p>

              <p
                v-if="showEcashUsed"
                class="h4 text-right text-success"
              >
                {{ $t('ecash utilizado:') }}
                <span class="h3">
                  {{ order.rewardsAmountUsed | currency }}
                </span>
              </p>

              <p
                v-if="getOrderTotalDiscount > 0"
                class="h4 text-right"
              >
                {{ $t('Desconto Total:') }}
                <span class="h3">
                  {{ getOrderTotalDiscount | currency }}
                </span>
              </p>

              <p
                v-if="order.comission > 0"
                class="h4 text-right"
              >
                <e-instructions
                  :title="$t('Comissão do garçom')"
                  :instruction="$t('Valor da porcentagem da gorjeta do garçom.')"
                />
                {{ $t('Comissão:') }}
                <span class="h3">
                  {{ order.comission | currency }}
                </span>
              </p>

              <p class="h3 text-right">
                {{ $t('Total do pedido:') }}
                <span class="h2">
                  {{ getOrderTotalValue | currency }}
                </span>
              </p>
            </div>
          </b-col>
        </b-row>

        <b-row
          v-if="getReturnedProducts && getReturnedProducts.length"
          class="my-2"
        >
          <b-col>
            <p class="h5">
              {{ $t('Produtos devolvidos') }}:
            </p>
            <b-table
              show-empty
              striped
              class="bordered"
              :fields="itemsReturnedFields"
              :items="getReturnedProducts"
            >
              <template #custom-foot>
                <tr>
                  <th
                    colspan="4"
                    class="text-right"
                  >
                    Total
                  </th>
                  <th class="text-right">
                    {{ getTotalReturnedProducts | currency }}
                  </th>
                </tr>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        v-if="showProductsToReclaim"
        :show-loading="loading || loadingProducts"
        action-collapse
        :title="$t('Retorno de produtos')"
      >
        <b-row>
          <b-col>
            <!-- qnd a tabela é responsiva o datepicker fica oculto. -->
            <b-table
              show-empty
              :responsive="isReadOnly"
              striped
              class="bordered"
              :empty-text="$t('Nenhum produtos para retorno registrado')"
              :fields="toReclaimFields"
              :items="itemsToReclaim"
            >
              <template #cell(returnDateLimit)="row">
                <p
                  v-if="isReadOnly"
                  class="m-0"
                >
                  {{ row.item.returnDateLimit | date }}
                </p>
                <FormulateInput
                  v-else
                  :id="`reclaim_table-date_limit_${
                    row.item.productAssociated ? row.item.productAssociated.id : ''
                  }_${row.item.localItemId || row.item.itemId}`"
                  v-model="row.item.returnDateLimit"
                  type="datepicker"
                  placeholder="dd/mm/yyyy"
                  :aria-label="$t('Data limite')"
                  :min="today"
                  validation="required"
                  :validation-messages="{
                    required: $t('Data limite é obrigatório.'),
                  }"
                  @input="handleDateToReturnChange(row.index, row.item.returnDateLimit)"
                />
              </template>

              <template #cell(name)="row">
                <sup class="m-0 text-primary">
                  {{ row.item.productAssociated.name }}
                </sup>

                <p class="m-0">
                  {{ row.item.skuProduct.name }}
                </p>
              </template>

              <template #cell(quantity)="row">
                {{ row.item.quantity - row.item.returnQuantityDamaged }}
              </template>

              <template #cell(contractualFine)="row">
                {{ $options.filters.currency(row.item.skuProduct.contractualFine) }}
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        v-if="order.id"
        :show-loading="loading || loadingProducts"
        action-collapse
        :title="$t('Pagamentos')"
      >
        <b-row>
          <b-col>
            <b-table
              class="bordered"
              striped
              show-empty
              :empty-text="
                isPix ? $t('Aguardando pagamento PIX') : $t('Nenhum pagamento registrado.')
              "
              :fields="paymentFields"
              :items="
                order.payments
                  ? order.payments.filter(
                    (payment) => !payment.canceled || payment.paymentMethodInterface === 'Online'
                  )
                  : []
              "
            >
              <template #cell(paymentMethod)="row">
                <b-badge
                  v-if="row.item.canceled"
                  variant="danger"
                >
                  {{ $t('Cancelado') }}
                </b-badge>
                <span>
                  {{ row.item.paymentMethod.name }}
                </span>
                <template v-if="row.item.cardBrand || row.item.nsu">
                  (
                  <template v-if="row.item.paymentMethodInterface">
                    {{ paymentMethodInterfaceLabel[row.item.paymentMethodInterface] || '-' }}
                  </template>

                  <template v-if="row.item.cardBrand">
                    {{ row.item.cardBrand || '-' }}
                  </template>

                  <template v-if="row.item.nsu">
                    <b>NSU: </b> {{ row.item.nsu || '-' }}
                  </template>
                  )
                  <span
                    v-if="
                      order.onlinePaymentCancelation &&
                        order.onlinePaymentCancelation.cancelRequestId
                    "
                  >&nbsp;-&nbsp;<b>{{ $t('Id do cancelamento: ') }}</b>
                    {{ order.onlinePaymentCancelation.cancelRequestId }}</span>
                </template>

                <!--
                  Parcelamento do crédito. Na retaguarda ele só aparece aqui: a coluna de valor
                  mostra o total cobrado, que sozinho não distingue uma venda à vista de uma
                  parcelada com acréscimo.
                -->
                <div
                  v-if="installmentCountOf(row.item) > 1"
                  class="small text-muted"
                >
                  <b>{{ $t('Parcelamento') }}: </b>{{ installmentLabel(row.item) }}
                </div>
                <div
                  v-if="row.item.surchargeValue > 0"
                  class="small text-muted"
                >
                  <b>{{ $t('Acréscimo') }}: </b>{{ row.item.surchargeValue | currency(null, 2) }}
                </div>
              </template>
            </b-table>
          </b-col>
        </b-row>
      </b-card-actions>

      <b-row v-if="order.origin === 'App' || order.origin === 'Website'">
        <b-col md="6">
          <b-card-actions
            v-if="order.id && (order.status === 'Completed' || order.status === 'Canceled')"
            :show-loading="loading || loadingProducts"
            action-collapse
            :title="$t('Avaliação do cliente (NPS)')"
          >
            <h5 v-if="!hasNpsAnswers">
              {{ $t('O cliente ainda não respondeu à pesquisa NPS') }}
            </h5>
            <div v-else>
              <FormulateInput
                id="nps_average"
                v-model="order.npsAverage"
                type="star-rating"
                :label="$t('Nota final')"
                :show-number="true"
              />
              <FormulateInput
                v-for="(npsAnswer, index) in order.npsAnswers.filter((answer) => answer.answer)"
                :key="`nps_question_${index}`"
                v-model="npsAnswer.answer"
                :type="npsAnswer.questionType === 'Rating' ? 'star-rating' : 'label'"
                :label="npsAnswer.question"
              />
              <FormulateInput
                id="nps-solved"
                v-model="order.npsSolved"
                name="issueSolved"
                type="switch"
                :label="$t('Foi resolvido?')"
                @input="saveNpsSolved"
              />
            </div>
          </b-card-actions>
        </b-col>
        <b-col
          v-if="hasNpsAnswers"
          md="6"
        >
          <b-card-actions
            v-if="order.id && (order.status === 'Completed' || order.status === 'Canceled')"
            :show-loading="loading || loadingProducts"
            action-collapse
            :title="$t('Feedback do NPS')"
          >
            <h5 v-if="!order.npsFeedbacks.length">
              {{ $t('Ainda não houve feedback da loja') }}
            </h5>
            <div v-else>
              <p
                v-for="(event, index) in order.npsFeedbacks"
                :key="`nps_event_${index}`"
              >
                <em>{{ event.createdDate | datetime }} ({{ event.createdBy }}):</em>&nbsp;<span>{{
                  event.description
                }}</span>
              </p>
            </div>
            <b-row class="mt-1">
              <b-col
                md="12"
                align="right"
              >
                <e-button
                  v-if="isReadOnly"
                  id="add_nps_feedback"
                  type="button"
                  variant="primary"
                  icon="plus"
                  :text="$t('Adicionar feedback')"
                  :title="
                    !$can('AddNpsFeedback', 'Sale')
                      ? $t('Você não possui permissão para adicionar feedback')
                      : ''
                  "
                  @click="addNpsFeedback"
                />
              </b-col>
            </b-row>
          </b-card-actions>
        </b-col>
      </b-row>
    </FormulateForm>

    <fab
      :main-tooltip="$t('Ações')"
      main-icon="keyboard_command_key"
      :actions="actions"
      :fixed-tooltip="true"
      :busy="loading"
      @cancel="onCancel"
      @save-completed="saveOrder"
      @print-order="onPrint"
      @print-lending="onPrintLendingContract"
      @print-lending-delivery="onPrintLendingDelivery"
      @print-consigned-returned="onPrintConsignedReturned"
      @print-rent="onPrintRentContract"
      @print-sale-rent="onPrintSaleRent"
      @cancel-order="onCancelOrder"
      @release-sale-locked="payBoxSaleOrderRelease"
    />

    <div class="pb-1" />

    <e-modal-order-cancelation
      ref="modalCancelation"
      @after-confirm="$router.push({ name: 'order-list' })"
    />

    <modal-add-nps-feedback
      ref="modalAddNpsFeedback"
      @after-confirm="addNpsFeedbackData"
    />

    <reward-code-verification-modal
      ref="codeVerificationModal"
      :show-virtual-keyboard="false"
      :show-overlay-loading="true"
      @verified="onCodeVerified"
      @cancelled="onCodeVerificationCancelled"
    />

    <sale-product-sidebar
      v-if="order.storeId"
      ref="saleProductSidebar"
      :store-id="order.storeId"
      :price-table-id="order.priceTableId"
      :items-added="order.products"
      @update="onUpdateProduct"
      @add="onAddProduct"
    />

    <bar-tab-consumption-info-sidebar
      ref="barTabConsumptionSidebar"
      is-history-view
      read-only
      @show-sale="onHideConsumptionSidebar"
    />

    <delivery-estimate-side-bar
      ref="deliveryEstimateSideBar"
      :order="order"
      @delivery-order-created="refresh"
    />
  </section>
</template>

<script>
import { debounce } from 'lodash'
import { BRow, BCol, BTable, BBadge, BCardTitle, BAlert, BButton } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  deliveryDomain,
  discountTypes,
  statusTypes,
  paymentTypes,
  loadingOverlay,
  saleDomain,
  invoiceDomains,
  approvalFlowDomains,
} from '@/mixins'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import EButton from '@/views/components/EButton.vue'
import EPersonSearch from '@/views/components/inputs/EPersonSearch.vue'
import EAddress from '@/views/components/EAddress.vue'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import EInstructions from '@/views/components/EInstructions.vue'
import moment from 'moment'
import fab from '@/views/components/FAB.vue'
import isElectron from 'is-electron'
import ESlotAuthenticated from '@/views/components/ESlotAuthenticated.vue'
import delegablePermissions from '@/utils/delegable-permissions'
import SaleProductSidebar from '../components/SaleProductSidebar.vue'
import SaleProductTable from '../components/SaleProductTable.vue'
import DeliveryTiersPreview from '../components/DeliveryTiersPreview.vue'
import EModalOrderCancelation from './EModalOrderCancelation.vue'
import BarTabConsumptionInfoSidebar from '../../pdv/pay-box-bar/BarTabConsumptionInfoSidebar.vue'
import ModalAddNpsFeedback from '../components/ModalAddNpsFeedback.vue'
import DeliveryEstimateSideBar from '../delivery-dashboard/components/DeliveryEstimateSideBar.vue'
import RewardCodeVerificationModal from '../../pdv/atm/components/modals/RewardCodeVerificationModal.vue'

export default {
  name: 'OrderMaintain',
  components: {
    BRow,
    BCol,
    BBadge,
    BCardTitle,
    BAlert,
    BButton,
    BCardActions,
    EStoreCombo,
    EButton,
    SaleProductSidebar,
    EAddress,
    EStatusBadge,
    SaleProductTable,
    DeliveryTiersPreview,
    BTable,
    EInstructions,
    fab,
    EModalOrderCancelation,
    EPersonSearch,
    ESlotAuthenticated,
    BarTabConsumptionInfoSidebar,
    ModalAddNpsFeedback,
    DeliveryEstimateSideBar,
    RewardCodeVerificationModal,
  },

  mixins: [
    discountTypes,
    statusTypes,
    deliveryDomain,
    saleDomain,
    paymentTypes,
    invoiceDomains,
    loadingOverlay,
    approvalFlowDomains,
  ],

  data() {
    return {
      loading: false,
      loadingProducts: false,
      isToUpdatePrice: true,
      localDeliveryStatusLabel: null,
      clientChangeEnabled: false,
      previousMainDate: null,
      pendingRewardAmount: undefined,
      ecashVerificationLoading: false,
      ecashDiscountAuth: null,
      // Dia (YYYY-MM-DD) usado na última resolução da config de delivery, para não repetir a
      // leitura quando a data de entrega não mudou de dia.
      deliveryConfigResolvedDay: undefined,
      // Grade de agendamento de chope/comodato do dia de entrega escolhido (null = sem grade).
      lendingSlotsDay: null,
    }
  },

  computed: {
    ...mapState('app', ['modalAuthRef']),
    ...mapState('auth', ['userData']),
    ...mapState('pages/sale/order/orderMaintain', [
      'order',
      'deliveryData',
      'minKilometerDelivery',
      'isQuotationReceived',
      'itemsToReclaim',
    ]),
    ...mapGetters('pages/sale/order/orderMaintain', [
      'getProducts',
      'hasLendingProducts',
      'hasRentProducts',
      'hasReturnedProducts',
      'hasItemsReturned',
      'getReturnedProducts',
      'getTotalReturnedProducts',
      'getCustomerAddressOptions',
      'getCustomerTelephone',
      'getOrderTotalValue',
      'getOrderTotalDiscount',
      'getOrderTotalDeliveryTax',
      'getDeliveryTierBaseValue',
      'getMaxScheduleDays',
      'getDateOfMaxSchedule',
      'hasColdOptionProducts',
      'saleOrderIsLocked',
    ]),
    ...mapGetters('common/priceTables', ['getComboPriceTables', 'getAllComboPriceTables']),
    ...mapState('reward', {
      rewardBalance: 'rewardBalance',
      rewardConfiguration: 'rewardConfiguration',
    }),
    ...mapGetters('reward', ['hasRewardBalance', 'formatRewardBalance']),
    today() {
      return new Date()
    },
    // A classificação é sempre a da tabela de preço do pedido (o backend aplica ao gravar).
    // Pedido somente leitura mostra a classificação gravada na venda.
    orderEchopeClassification() {
      if (this.isReadOnly) return this.order.echopeClassification

      const priceTable = this.getAllComboPriceTables.find(
        pt => String(pt.id) === String(this.order.priceTableId)
      )
      return priceTable?.echopeClassification ?? this.order.echopeClassification
    },
    isEdit() {
      return !!this.$route.params.id
    },
    applyEcashPermission() {
      return delegablePermissions.ERP_ORDER_AUTHORIZE_ECASH_USAGE_FOR_BUSINESS_CUSTOMER
    },
    isJuridicalCustomer() {
      return this.order?.customer?.personType === 'Juridical'
    },

    // Motoboy próprio designado à venda. O bloco só aparece quando há designação — pedidos
    // entregues por fornecedor de entrega, ou ainda sem motoboy, não exibem nada.
    hasMotoboy() {
      return !!(this.order?.motoboy || this.order?.motoboyId)
    },
    motoboyName() {
      return this.order?.motoboy?.name || '-'
    },
    motoboyTelephone() {
      const telephone = this.order?.motoboy?.telephone
      return telephone ? this.$options.filters.phone(telephone) : '-'
    },
    motoboyDocument() {
      return this.order?.motoboy?.document || '-'
    },

    isReadOnly() {
      return (
        !!this.$route.name.includes('read-only') ||
        this.order.status === this.saleStatusEnum.COMPLETED
      )
    },
    isCreating() {
      return !this.isEdit && !this.isReadOnly
    },
    showEcashUsed() {
      return this.isReadOnly && this.order.rewardsAmountUsed > 0
    },
    hasNpsAnswers() {
      return this.order.npsAnswers?.length > 0
    },
    isPix() {
      return this.order?.onlinePayment?.paymentMethod === 'Pix'
    },
    canPrintLendingContract() {
      return isElectron() && (this.isEdit || this.isReadOnly) && this.hasLendingProducts
    },
    canPrintRentContract() {
      return isElectron() && (this.isEdit || this.isReadOnly) && this.hasRentProducts
    },
    canPrintSaleRent() {
      return (
        isElectron() &&
        (this.isEdit || this.isReadOnly) &&
        this.hasRentProducts &&
        this.order.status === 'Completed'
      )
    },
    canPrintConsignedReturned() {
      return isElectron() && (this.isEdit || this.isReadOnly) && this.hasItemsReturned
    },

    canEditDelivery() {
      return !this.isReadOnly && !this.isConsignedWithPayment
    },
    titleNotEditDelivery() {
      return !this.isReadOnly && this.isEdit && this.isConsignedWithPayment
        ? this.$t('Não é possível alterar se pedido consignado já possui pagamento registrado')
        : ''
    },
    isDelivery() {
      return this.order.isDelivery
    },
    isDeliveryPerKilometer() {
      return this.deliveryData.deliveryFeeType === this.deliveryFeeEnum.FEE_PER_KILOMETER
    },

    showProductsToReclaim() {
      return this.itemsToReclaim?.length > 0
    },

    showDeliveryData() {
      return this.order.isDelivery || this.order.isConsigned
    },
    // Pedido com entrega e comodato: usa a agenda de instalação e pode ser reagendado na edição.
    isLendingSchedule() {
      return !!this.order.isDelivery && this.hasLendingProducts
    },
    showLendingSlots() {
      return (
        this.showDeliveryData &&
        this.isLendingSchedule &&
        !!this.lendingSlotsDay?.installationMinutes
      )
    },
    // Chave do que define a grade exibida: loja, dia de entrega e se o pedido é de comodato.
    lendingSlotsKey() {
      const { date } = this.order.deliveryDate ?? {}
      const day = date && moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : ''
      return `${this.order.storeId || ''}|${day}|${this.isLendingSchedule}`
    },
    showCancelData() {
      return this.order?.status === this.saleStatusEnum.CANCELED
    },

    isConsignedWithPayment() {
      return this.order.isConsigned && this.order.payments?.length > 0
    },

    isWaitingForApproval() {
      return this.order.status === this.saleStatusEnum.WAITING_APPROVAL
    },

    isDiscountPercent() {
      return this.order.discountType === this.discountTypesEnum.PERCENTAGE
    },

    getDiscountSymbol() {
      return this.isDiscountPercent ? this.$t('%') : this.$t('R$')
    },
    getCancelationUser() {
      return this.order?.cancelationUser?.name || this.$t('Não informado')
    },
    getDaysOfWeekendWithoutDelivery() {
      // Mapa { indiceDoDia: true } dos dias sem delivery, resolvido da configuração de delivery da
      // loja (o antigo Store.openingHours[dia].delivery foi removido e migrou para o agregado).
      return this.order.store?.deliveryDisabledWeekdays || {}
    },

    // Mapa { indiceDoDia: { deliveryEnabled, startTime, endTime } } das janelas de entrega da loja.
    getDeliveryWindows() {
      return this.order.store?.deliveryWindows || {}
    },

    // A config de delivery resolvida da loja não pôde ser lida: as taxas estão zeradas.
    deliveryFeesUnavailable() {
      return !!this.order.store?.deliveryFees?.unavailable
    },

    // Instante de referência para resolver a configuração de delivery: a data/hora de entrega
    // escolhida. As faixas de taxa, a janela e a campanha vigente variam por DIA, então resolver
    // sem essa referência aplicaria as faixas de outro dia da semana. Sem data escolhida devolve
    // null, e o backend resolve por um dia representativo.
    deliveryReferenceDate() {
      const { date, time } = this.order.deliveryDate ?? {}
      if (!date) return null

      const day = moment(date)
      if (!day.isValid()) return null

      const reference = moment(
        `${day.format('YYYY-MM-DD')} ${time || moment().format('HH:mm')}`,
        'YYYY-MM-DD HH:mm'
      )
      return reference.isValid() ? reference.format() : null
    },

    // Janela de entrega (startTime/endTime) do dia da data de entrega selecionada, ou null.
    selectedDeliveryWindow() {
      const { date } = this.order.deliveryDate
      if (!date) return null
      const weekday = moment(date).day()
      const window = this.getDeliveryWindows[weekday]
      if (!window || !window.deliveryEnabled || !window.startTime || !window.endTime) return null
      return window
    },

    deliveryTimeInstruction() {
      const window = this.selectedDeliveryWindow
      if (!window) return this.$t('Selecione a data de entrega para ver o horário disponível.')
      return `${this.$t('Horário de entrega da loja')}: ${window.startTime} - ${window.endTime}`
    },

    showDeliveryAddressCard() {
      return (
        this.order.customer !== null &&
        this.order.isDelivery &&
        this.order.addressSelected?.id === -1
      )
    },

    dataCardTitle() {
      const title = `${this.$t('Dados do pedido')}${this.order?.id ? ` #${this.order.id}` : ''}`
      if (this.order.quotationId)
        return `${title} (${this.$t('Criado a partir da cotação: ')} ${this.order.quotationId})`
      return title
    },

    customerDefaultOptions() {
      const customer = this.order?.customer
      if (customer && Object.keys(customer).length > 0) return [customer]
      return []
    },

    discountPermission() {
      return delegablePermissions.ERP_ORDER_DISCOUNT
    },
    deliveryDiscountPermission() {
      return delegablePermissions.ERP_ORDER_DELIVERY_DISCOUNT
    },

    itemsReturnedFields() {
      return [
        {
          label: this.$t('Produto'),
          key: 'skuProduct.name',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Data da devolução'),
          key: 'createdDate',
          tdClass: 'text-center',
          thStyle: { width: '180px' },
          formatter: value => this.$options.filters.datetime(value),
        },
        {
          label: this.$t('Qtd.'),
          key: 'quantity',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '60px' },
        },
        {
          label: this.$t('Valor unitário'),
          key: 'price',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '120px', minWidth: '120px' },
          formatter: (value, index, item) =>
            this.$options.filters.currency(item.priceRuleSelected.priceInfo.price),
        },
        {
          label: this.$t('Total'),
          key: 'netValue',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '120px', minWidth: '120px' },
          formatter: (value, index, item) =>
            this.$options.filters.currency(item.priceRuleSelected.priceInfo.localNetValue),
        },
      ]
    },

    paymentFields() {
      return [
        {
          label: this.$t('Tipo'),
          key: 'paymentMethod',
          thClass: 'text-center',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Valor'),
          key: 'value',
          thClass: 'text-center',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
          formatter: (value, index, item) =>
            this.$options.filters.currency(value - (item.change || 0)),
        },
      ]
    },

    toReclaimFields() {
      return [
        {
          label: this.$t('Data limite'),
          key: 'returnDateLimit',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '180px' },
        },
        {
          label: this.$t('Data do retorno'),
          key: 'returnDate',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '180px' },
          formatter: (value, index, item) =>
            value
              ? `${this.$options.filters.date(value)} - ${item.returnUser?.name}`
              : this.$t('Pendente'),
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          thClass: 'text-center',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Valor unitário indenização'),
          key: 'contractualFine',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '160px', minWidth: '160px' },
        },
        {
          label: this.$t('Qtd. do retorno'),
          key: 'quantity',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Qtd. avariado'),
          key: 'returnQuantityDamaged',
          thClass: 'text-center',
          tdClass: 'text-center',
          thStyle: { width: '100px' },
          hide: !this.isReadOnly,
        },
      ].filter(f => !f.hide)
    },

    actions() {
      return [
        {
          name: 'cancel',
          icon: 'arrow_back',
          color: 'red',
          tooltip: this.$t('Voltar'),
        },
        {
          name: 'save-completed',
          icon: 'save',
          color: 'green',
          tooltip: this.$t('Salvar'),
          hide: this.isReadOnly,
        },
        {
          name: 'cancel-order',
          icon: 'cancel',
          color: 'red',
          tooltip: this.$t('Cancelar Pedido'),
          hide:
            this.order?.status === 'Canceled' ||
            !this.order?.id ||
            this.order.origin === this.saleOriginEnum.INVOICE,
        },
        {
          name: 'print-order',
          icon: 'print',
          color: 'green',
          tooltip: this.$t('Imprimir'),
          hide:
            !this.canPrintOrder() ||
            this.order.origin === this.saleOriginEnum.INVOICE ||
            !isElectron(),
        },
        {
          name: 'print-lending',
          icon: 'print',
          color: 'green',
          tooltip: this.$t('Imprimir Contrato Comodato'),
          hide: !this.canPrintLendingContract,
        },
        {
          name: 'print-lending-delivery',
          icon: 'print',
          color: 'green',
          tooltip: this.$t('Imprimir Recibo De Entrega Comodato'),
          hide: !this.canPrintLendingContract,
        },
        {
          name: 'print-consigned-returned',
          icon: 'print',
          color: 'green',
          tooltip: this.$t('Imprimir Recibo Devolução'),
          hide: !this.canPrintConsignedReturned,
        },
        {
          name: 'print-rent',
          icon: 'print',
          color: 'green',
          tooltip: this.$t('Imprimir Contrato Aluguel'),
          hide: !this.canPrintRentContract,
        },
        {
          name: 'print-sale-rent',
          icon: 'print',
          color: 'green',
          tooltip: this.$t('Imprimir Recibo Aluguel'),
          hide: !this.canPrintSaleRent,
        },
        {
          name: 'release-sale-locked',
          icon: 'lock_open',
          color: 'green',
          tooltip: this.$t('Liberar Venda Bloqueada'),
          hide: !this.saleOrderIsLocked,
        },
      ].filter(a => !a.hide)
    },

    consumptionIdLabelIcons() {
      if (!this.order.barTabConsumptionId) return []

      return [
        {
          eventName: 'link',
          icon: 'eye-fill',
          clazz: '',
          variant: '',
          title: this.$t('Mostrar dados do consumo'),
        },
      ]
    },
  },
  watch: {
    // Valida a hora contra a janela da loja assim que o usuário altera hora/data (o timepicker
    // nativo não propaga @blur de forma confiável). Só em pedidos novos, para não limpar a hora
    // já salva de um pedido em edição caso a configuração da loja tenha mudado.
    'order.deliveryDate.time': function watchDeliveryTime() {
      if (!this.isEdit) this.validateDeliveryTime()
    },
    // Mudar a data de entrega muda o dia da semana e a campanha vigente: reresolve a config de
    // delivery da loja para a nova data (faixas de taxa e janela) antes de validar a hora.
    'order.deliveryDate.date': async function watchDeliveryDate() {
      if (this.isEdit) return
      await this.resolveStoreDeliveryConfig({ keepDeliveryDate: true })
      this.validateDeliveryTime()
    },
    lendingSlotsKey() {
      this.fetchLendingSlots()
    },
  },
  async mounted() {
    this.fetchPriceTables()
    try {
      this.loading = true
      this.fetchMinKilometerParam()
      this.isToUpdatePrice = false
      if (this.isEdit || this.isReadOnly) {
        await this.stFetchOrderById(this.$route.params.id)
        if (!this.isReadOnly) {
          await this.stUpdateDeliveryTax()

          if (!this.saleOrderIsLocked && (await this.stApplyCustomerPriceTableIfInactive())) {
            this.showWarning({
              message: this.$t(
                'A tabela de preço do pedido estava inativa. Foi aplicada a tabela de preço do cliente e os preços foram recalculados. Revise e salve o pedido.'
              ),
            })
          }
        }
        await this.getCustomerRewardBalance(this.order.customer?.id)

        this.localDeliveryStatusLabel = this.deliveryStatusLabel[this.order.deliveryStatus]
      }

      if (this.isQuotationReceived) {
        await this.stQuotationToOrder()
      }
      await new Promise(resolve => setTimeout(() => resolve(), 500))
    } catch (err) {
      this.showError({ message: err.message })
    } finally {
      this.loading = false
      if (!this.isConsignedWithPayment || !this.isReadOnly) this.isToUpdatePrice = true
    }
    this.previousMainDate = null
  },

  methods: {
    installmentCountOf(payment) {
      return Number(payment?.installmentCount) || 1
    },

    /**
     * Descrição do parcelamento de um pagamento.
     *
     * O valor da parcela não vem do servidor (não é coluna de SalePayment), então é
     * reconstruído truncando em duas casas — a mesma regra do rateio, que trunca e joga o resto
     * numa parcela só. Dividir direto produziria dízima (R$ 27,0417) e mostraria um valor que o
     * cliente nunca viu. Quando o resto não é zero, uma das parcelas difere desse valor em
     * centavos; por isso o total acompanha a linha.
     *
     * O texto é montado com template literal: o $t deste projeto só interpola em chaves de
     * tradução registradas, e português cru como chave voltaria com os placeholders literais.
     */
    installmentLabel(payment) {
      const count = this.installmentCountOf(payment)
      const total = (payment?.value || 0) - (payment?.change || 0)
      const perInstallment = count > 0 ? Math.floor((total / count) * 100) / 100 : total
      const currency = amount => this.$options.filters.currency(amount, null, 2)

      return `${count}x ${this.$t('de')} ${currency(perInstallment)} — ${this.$t(
        'total'
      )} ${currency(total)}`
    },

    ...mapActions('common/priceTables', ['fetchPriceTables']),
    ...mapActions('pages/pdv/payBox', {stpayBoxSaleOrderRelease: 'payBoxSaleOrderRelease'}),
    ...mapActions('pages/sale/order/orderMaintain', {
      stFetchOrderById: 'fetchOrderById',
      stFetchStoreById: 'fetchStoreById',
      stFetchCustomerById: 'fetchCustomerById',
      stApplyCustomerPriceTableIfInactive: 'applyCustomerPriceTableIfInactive',
      stSetOrderStoreId: 'setOrderStoreId',
      stSaveOrder: 'saveOrder',
      stAddItem: 'addProduct',
      stUpdateItem: 'updateProduct',
      stRemoveItem: 'removeProduct',
      stSetDeliveryAddress: 'setDeliveryAddress',
      stCleanState: 'cleanState',
      stCleanDeliveryAddress: 'cleanDeliveryAddress',
      stCleanInputsAuth: 'cleanInputsAuth',
      stCleanOrderCustomer: 'cleanOrderCustomer',
      stUpdateProductPrices: 'updateProductPrices',
      stQuotationToOrder: 'quotationToOrder',
      stUpdateDeliveryTax: 'updateDeliveryTax',
      updateCustomer: 'updateCustomer',
      stResetToOriginal: 'resetToOriginal',
      stSetDeliveryDateAuth: 'setDeliveryDateAuth',
      stFetchLendingDeliverySlots: 'fetchLendingDeliverySlots',
      fetchMinKilometerParam: 'fetchMinKilometerParam',
      stAddNpsFeedback: 'addNpsFeedback',
    }),
    ...mapActions('pages/sale/order', ['printOrder', 'fetchSaleById']),
    ...mapActions('pages/sale/clients/clientMaintain', {
      stClientMaintainCleanState: 'cleanState',
    }),
    ...mapActions('reward', [
      'getCustomerRewardBalance',
      'getRewardConfiguration',
      'calculateRewardDiscount',
    ]),

    async refresh() {
      try {
        this.loading = true
        await this.stFetchOrderById(this.$route.params.id)
      } catch (err) {
        this.showError({ message: err.message })
      } finally {
        this.loading = false
      }
    },

    addNpsFeedback() {
      this.$refs.modalAddNpsFeedback.show(this.order.id)
    },

    addNpsFeedbackData(feedbackData) {
      this.stAddNpsFeedback({
        createdBy: this.userData?.fullName,
        createdDate: Date.now(),
        ...feedbackData,
      })
    },

    onShowProductSidebar(item) {
      this.$refs.saleProductSidebar.show(item)
    },

    onAddProduct(formData) {
      this.stAddItem({ formData })
    },
    onUpdateProduct(formData) {
      this.stUpdateItem({ formData })
    },
    async onRemoveProduct(item) {
      const confirmed = await this.confirm()
      if (confirmed) {
        this.stRemoveItem({ id: item.id || item.localId })
      }
    },

    canPrintOrder() {
      if (!isElectron()) {
        return false
      }
      if (!this.isReadOnly) {
        return false
      }

      return true
    },

    async onPrint() {
      try {
        this.busy = true
        this.showLoadingOverlay(this.$t('Buscando dados para impressão'))
        const orderPrint = await this.fetchSaleById(this.order.id)

        const validToPrint = this.mxValidatePrintForOnlineSale(orderPrint)
        if (!validToPrint.valid) {
          this.showWarning({ message: validToPrint.message })
          return
        }

        this.showLoadingOverlay(this.$t('Imprimindo cupom'))
        await this.onPrintLendingDelivery()
        await this.printOrder({ orderPrint, template: 'SALE_ORDER' })

        this.showSuccess({ message: '' })
      } catch (err) {
        /* eslint-disable no-console */
        console.error('print:', err)
        this.busy = false
      } finally {
        this.hideLoadingOverlay()
      }
    },

    async onPrintLendingContract() {
      if (!this.hasLendingProducts) {
        return
      }
      await this.onManagePrint('Imprimindo Contrato Comodato', 'LENDING_CONTRACT')
    },

    async onPrintLendingDelivery() {
      if (!this.hasLendingProducts) {
        return
      }
      await this.onManagePrint('Imprimindo Recibo Entrega Comodato', 'LENDING_DELIVERY')
    },

    async onPrintRentContract() {
      if (!this.hasRentProducts) {
        return
      }
      await this.onManagePrint('Imprimindo Contrato Aluguel', 'RENT_CONTRACT')
    },

    async onPrintSaleRent() {
      if (!this.hasRentProducts) {
        return
      }
      await this.onManagePrint('Imprimindo Recibo de Aluguel', 'SALE_RENT')
    },

    async onPrintConsignedReturned() {
      if (!this.hasItemsReturned) {
        return
      }

      if (this.order.status !== 'Completed') {
        this.showWarning({
          message: 'O Recibo de devolução somente poderá ser impresso em vendas finalizadas.',
        })
        return
      }

      await this.onManagePrint('Imprimindo Recibo de Devolução', 'SALE_RETURN')
    },

    async onCancelOrder() {
      if (
        this.order?.invoice?.model === 'NFE' &&
        this.order?.invoice?.invoiceCategory === 'SaleFromPdv'
      ) {
        this.showInvalidDataMessage({
          message: this.$t(
            'Existe uma NF-e atrelada a esta venda! Favor realizar o cancelamento pelo PDV.'
          ),
        })
        return
      }
      this.$refs.modalCancelation.show(this.order.id, this.order)
    },

    async onManagePrint(title, template) {
      try {
        this.busy = true
        this.showLoadingOverlay(this.$t(title))
        const orderPrint = await this.fetchSaleById(this.order.id)
        await this.printOrder({ orderPrint, template })

        this.showSuccess({ message: '' })
      } catch (err) {
        /* eslint-disable no-console */
        console.error('print:', err)
        this.busy = false
      } finally {
        this.hideLoadingOverlay()
      }
    },

    async saveOrder() {
      if (this.loadingProducts) {
        this.showWarning({ message: this.$t('Aguarde o carregamento de produtos.') })
        return
      }

      this.$refs.orderForm.showErrors()
      if (this.$refs.orderForm.hasErrors) {
        this.showInvalidDataMessage()
        return
      }

      if (!this.isEdit && this.showDeliveryData) {
        const momentDate = moment(this.order.deliveryDate.date)?.format('YYYY-MM-DD')
        const time = this.order.deliveryDate.time || moment().format('HH:mm')
        const deliveryDate = moment(`${momentDate} ${time}`, 'YYYY-MM-DD HH:mm')

        if (deliveryDate.isValid() && deliveryDate.isSameOrBefore(this.today)) {
          this.showInvalidDataMessage({
            message: this.$t('Data de entrega não pode ser retroativa.'),
          })
          return
        }

        // Rede de segurança: bloqueia salvar com hora de entrega fora da janela da loja.
        if (!this.validateDeliveryTime()) return
      }

      // Sem a taxa da loja o pedido seria gravado com frete zerado.
      if (this.order.isDelivery && this.deliveryFeesUnavailable) {
        this.showInvalidDataMessage({ message: this.deliveryFeesUnavailableMessage() })
        return
      }

      try {
        this.loading = true
        await this.stSaveOrder()

        if (isElectron() && (this.hasLendingProducts || this.hasRentProducts)) {
          const confirmed = await this.confirm({
            title: this.$t('Contrato comodato/aluguel'),
            text: this.$t(
              'Venda possui itens de comodato e/ou aluguel, deseja imprimir o(s) contrato(s) agora ?'
            ),
            confirmButtonText: this.$t('Sim'),
            cancelButtonText: this.$t('Não'),
          })
          if (confirmed) {
            await this.onPrintLendingContract()
            await this.onPrintRentContract()
          }
        }
        this.showSuccess({
          message: this.$t(`${this.isEdit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
        })
        this.stCleanState()
        this.$router.push({ name: 'order-list' })
      } catch (err) {
        this.showError({ error: err })
      } finally {
        this.loading = false
      }
    },

    async onSelectStore(storeId) {
      if (!storeId || this.loading) return

      const oldStoreId = this.order.storeId
      if (oldStoreId && storeId !== oldStoreId) {
        const confirmed = await this.confirm({
          text: this.$t(
            'Ao alterar a loja, os valores de delivery e produtos podem ser atualizados e os campos que precisam de autorização terão valor e autorização resetados.'
          ),
        })

        if (!confirmed) {
          // necessário para forçar o combo a alterar o valor.
          await this.stSetOrderStoreId({ storeId: null })
          await this.stSetOrderStoreId({ storeId: oldStoreId })
          return
        }
        await this.stCleanInputsAuth()
      }

      await this.stSetOrderStoreId({ storeId })
      // Troca de loja invalida a resolução anterior, mesmo que a data de entrega não tenha mudado.
      this.deliveryConfigResolvedDay = undefined
      await this.resolveStoreDeliveryConfig()
      this.updatePrices()
    },

    // Resolve a configuração de delivery da loja para a DATA DE ENTREGA escolhida e reaplica a
    // taxa. Sem isso a tela mantinha a resolução feita na seleção da loja (um dia representativo
    // da semana), aplicando as faixas/campanha de outro dia.
    async resolveStoreDeliveryConfig({ keepDeliveryDate = false } = {}) {
      const { storeId } = this.order
      if (!storeId) return

      const { date } = this.order.deliveryDate ?? {}
      const day = date && moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : null
      if (day === this.deliveryConfigResolvedDay) return
      this.deliveryConfigResolvedDay = day

      await this.stFetchStoreById({
        storeId,
        referenceDate: this.deliveryReferenceDate,
        keepDeliveryDate,
      })
      if (this.deliveryFeesUnavailable) {
        // Libera nova tentativa na próxima troca de data/loja.
        this.deliveryConfigResolvedDay = undefined
        this.showError({ message: this.deliveryFeesUnavailableMessage() })
      }
      await this.onUpdateDelivery()
    },

    deliveryFeesUnavailableMessage() {
      return this.$t(
        'Não foi possível carregar a taxa de entrega da loja. Recarregue a página e tente novamente.'
      )
    },

    async onBlurDeliveryDate(val) {
      const date = moment(val)
      if (!date.isValid()) return

      if (date.toDate() > this.getDateOfMaxSchedule) {
        if (!this.order.storeId) {
          this.showWarning({ message: 'Favor selecionar a loja primeiro.' })
          await this.stResetToOriginal({ type: 'deliveryDate' })
          return
        }

        const confirmed = await this.confirm({
          title: this.$t('Alterar o data da entrega?'),
          text: this.$t('SALE_ORDER.ALERT.CONFIRM_DELIVERY_MAX_SCHEDULE', {
            maxDay: this.getMaxScheduleDays,
          }),
        })

        if (!confirmed) {
          await this.stResetToOriginal({ type: 'deliveryDate' })
          return
        }

        try {
          const delegateValue = await this.modalAuthRef.auth(
            delegablePermissions.ERP_ORDER_OVERFLOW_MAX_DELIVERY_DATE,
            this.order.storeId
          )
          this.stSetDeliveryDateAuth(delegateValue)
        } catch (error) {
          await this.stResetToOriginal({ type: 'deliveryDate' })
        }
      }

      // Trocar a data pode invalidar a hora já escolhida (janela varia por dia da semana).
      this.validateDeliveryTime()
    },
    onBlurDeliveryTime() {
      if (this.isReadOnly) return
      this.validateDeliveryTime()
    },

    async fetchLendingSlots() {
      const { storeId } = this.order
      const { date } = this.order.deliveryDate ?? {}
      if (!this.isLendingSchedule || !storeId || !date || !moment(date).isValid()) {
        this.lendingSlotsDay = null
        return
      }

      const key = this.lendingSlotsKey
      try {
        const day = await this.stFetchLendingDeliverySlots({
          storeId,
          date,
          ignoreSaleId: this.order.id || null,
        })
        // Descarta respostas atrasadas de uma loja/dia que já não estão selecionados.
        if (key === this.lendingSlotsKey) this.lendingSlotsDay = day
      } catch {
        // A agenda é apoio: sem ela a loja segue informando a hora manualmente.
        this.lendingSlotsDay = null
      }
    },
    // Hora (HH:mm) no fuso da loja, como veio da API.
    slotTime(value) {
      return moment.parseZone(value).format('HH:mm')
    },
    isSelectedLendingSlot(slot) {
      return this.order.deliveryDate?.time === this.slotTime(slot.start)
    },
    lendingSlotTitle(slot) {
      if (slot.available) return this.$t('Horário disponível')
      if (slot.unavailableReason === 'Occupied') {
        const orders = (slot.occupiedBySaleIds || []).map(id => `#${id}`).join(', ')
        return `${this.$t('Horário ocupado pelo pedido')} ${orders}`
      }
      return this.$t('Horário fora da antecedência mínima de entrega')
    },
    // No ERP o horário ocupado pode ser escolhido (sobreposição), com confirmação.
    async onSelectLendingSlot(slot) {
      if (!slot.available) {
        const confirmed = await this.confirm({
          title: this.$t('Horário indisponível'),
          text: `${this.lendingSlotTitle(slot)}. ${this.$t('Deseja agendar neste horário mesmo assim?')}`,
        })
        if (!confirmed) return
      }
      this.order.deliveryDate.time = this.slotTime(slot.start)
    },
    // Valida a hora de entrega contra a janela do dia da data selecionada. Fora da janela, avisa
    // e limpa a hora. Sem janela conhecida (loja sem horário resolvido) não bloqueia.
    validateDeliveryTime() {
      const window = this.selectedDeliveryWindow
      const { time } = this.order.deliveryDate
      if (!window || !time) return true

      if (!this.isTimeWithinWindow(time, window.startTime, window.endTime)) {
        this.showWarning({
          message: `${this.$t(
            'A hora de entrega deve estar dentro do horário da loja'
          )}: ${window.startTime} - ${window.endTime}.`,
        })
        this.order.deliveryDate.time = null
        return false
      }
      return true
    },
    isTimeWithinWindow(time, start, end) {
      const toMinutes = value => {
        const [hours, minutes] = String(value).split(':').map(Number)
        return hours * 60 + (minutes || 0)
      }
      const target = toMinutes(time)
      const from = toMinutes(start)
      const to = toMinutes(end)
      // Janela normal (from <= to) ou que cruza a meia-noite (ex.: 18:00–02:00).
      return from <= to ? target >= from && target <= to : target >= from || target <= to
    },
    onChangeDeliveryFeeType(val) {
      if (!val) return
      this.onUpdateDelivery()
    },
    onChangeDelivery(val) {
      if (val) this.onUpdateDelivery()
    },
    onChangeConsign() {
      this.onUpdateDelivery()
    },

    onUpdateDelivery() {
      if (this.isReadOnly || this.loading) return
      this.stUpdateDeliveryTax()
    },

    async onSelectCustomer(val) {
      if (val) {
        try {
          await this.stFetchCustomerById({ customerId: val })
          await this.updatePrices()
          // Buscar saldo de recompensas do cliente
          await this.getCustomerRewardBalance(val)
          // Buscar configurações de recompensa para obter o limite de uso
          await this.getRewardConfiguration()
        } catch (error) {
          this.showError({ error })
        }
      } else {
        this.stCleanOrderCustomer()
        this.rewardBalance = null
      }
    },

    // eslint-disable-next-line func-names
    updatePrices: debounce(async function () {
      if (!this.isToUpdatePrice) return

      try {
        this.loadingProducts = true
        await this.stUpdateProductPrices()
      } catch (error) {
        this.showError({ error })
      } finally {
        this.loadingProducts = false
      }
    }, 400),

    onCancel() {
      this.stCleanState()
      this.$router.push({ name: 'order-list' })
    },

    changeAddress(addressData) {
      if (addressData) this.stSetDeliveryAddress({ addressData })
      else this.stCleanDeliveryAddress()
    },

    formatDiscount(value, type) {
      return type === this.discountTypesEnum.PERCENTAGE
        ? this.$options.filters.percentage(value)
        : this.$options.filters.currency(value)
    },

    async createCustomer() {
      await this.stClientMaintainCleanState()
      this.$router.push({
        name: 'client-add',
        params: {
          callbackRouteName: 'order-add',
        },
      })
    },
    async changeClient() {
      if (this.clientChangeEnabled) {
        try {
          this.loading = true
          await this.updateCustomer()
          this.showSuccess({ message: this.$t('Cliente alterado com sucesso') })
          this.clientChangeEnabled = false
        } catch (error) {
          this.showError({ error })
        } finally {
          this.loading = false
        }
      } else {
        this.clientChangeEnabled = true
      }
    },

    async cancelChangeClient() {
      try {
        this.loading = true
        await this.stFetchOrderById(this.$route.params.id)
        this.clientChangeEnabled = false
      } catch (error) {
        this.showError({ error })
      } finally {
        this.loading = false
      }
    },

    async onToggleRewardCredits(useCredits) {
      if (useCredits && this.hasRewardBalance) {
        // Calcular desconto baseado no saldo disponível e limite de uso configurado
        const rewardDiscount = await this.calculateRewardDiscount({
          customerId: this.order.customer.id,
          saleTotal: this.getOrderTotalValue,
          priceTableId: this.order.priceTableId,
        })

        // Armazenar valor pendente e mostra modal de verificação
        this.pendingRewardAmount = rewardDiscount
        this.ecashVerificationLoading = true
        // Temporariamente set para true para não perder o estado, será revertido se cancelado
        this.order.useRewards = true
        this.$refs.codeVerificationModal.show(
          this.order.customer.id,
          this.getOrderTotalValue
        )
      } else {
        // Remover desconto de recompensa se desabilitado
        this.order.rewardsAmountUsed = 0
        this.order.useRewards = false
      }
    },

    async applyRewardCredits(useCredits) {
      if (useCredits && this.hasRewardBalance) {
        // Calcular desconto baseado no saldo disponível e limite de uso configurado
        const rewardDiscount = await this.calculateRewardDiscount({
          customerId: this.order.customer.id,
          saleTotal: this.getOrderTotalValue,
          priceTableId: this.order.priceTableId,
        })

        this.order.rewardsAmountUsed = rewardDiscount
      } else {
        // Remover desconto de recompensa se desabilitado
        this.order.rewardsAmountUsed = 0
        this.order.useRewards = false
      }
    },

    onCodeVerified() {
      // Código verificado com sucesso, aplicar desconto
      if (this.pendingRewardAmount !== undefined) {
        this.order.rewardsAmountUsed = this.pendingRewardAmount
        this.pendingRewardAmount = undefined
        this.ecashVerificationLoading = false
      }
    },

    onCodeVerificationCancelled() {
      // Usuário cancelou a verificação, reverter o switch
      this.order.useRewards = false
      this.order.rewardsAmountUsed = 0
      this.pendingRewardAmount = undefined
      this.ecashVerificationLoading = false
    },

    // referência: https://bootstrap-vue.org/docs/components/form-datepicker#disabling-dates
    disabledDeliveryDate(ymd, date) {
      // weekends (Sunday = `0`, Saturday = `6`)
      const weekday = date.getDay()
      // Return `true` if the date should be disabled
      return this.getDaysOfWeekendWithoutDelivery[weekday]
    },
    handleDateToReturnChange(productIndex, dateString) {
      if (productIndex === 0) {
        this.itemsToReclaim.forEach(item => {
          if (
            (!item.returnDateLimit || item.returnDateLimit === this.previousMainDate) &&
            dateString
          ) {
            // eslint-disable-next-line no-param-reassign
            item.returnDateLimit = dateString
          }
        })
        this.previousMainDate = dateString
      }
      return dateString
    },

    onShowConsumptionSidebar() {
      this.$refs.barTabConsumptionSidebar.show(this.order.barTabConsumptionId)
    },
    onHideConsumptionSidebar() {
      this.$refs.barTabConsumptionSidebar.hide()
    },
    async saveNpsSolved() {
      if (!this.loading) {
        try {
          this.loading = true
          await this.$http.put(
            `/api/sales/${this.order.id}/${this.order.npsSolved ? 'solve-nps' : 'unsolve-nps'}`
          ) // this.stFetchOrderById(this.$route.params.id)
        } catch (error) {
          this.showError({ error })
        } finally {
          this.loading = false
        }
      }
    },
    async onAskDeliveryService() {
      if (
        await this.confirm({
          text: this.$t('Deseja realizar a cotação para o serviço de entrega?'),
        })
      ) {
        this.$refs.deliveryEstimateSideBar.show(this.order.id)
      }
    },
    async payBoxSaleOrderRelease() {
      if (
        await this.confirm({
          text: this.$t('Deseja liberar a venda bloqueada no caixa?'),
        })
      ) {
        try {
          this.loading = true
          await this.stpayBoxSaleOrderRelease({ saleId: this.order.id, type: 'manual' })
          await this.stFetchOrderById(this.order.id)
          this.showSuccess({ message: this.$t('Venda liberada com sucesso') })
        } catch (error) {
          this.showError({ error })
        } finally {
          this.loading = false
        }
      }
    },
  },
}
</script>

<style lang="scss">
.lending-slot-unavailable {
  text-decoration: line-through;
  opacity: 0.6;
}

.clean-bootstrap-margin {
  margin-right: 0.25rem !important;
}
</style>
